import React from "react";
import DiscountIcon from "@mui/icons-material/Discount";
import InfoIcon from "@mui/icons-material/Info";

interface BillPreviewProps {
  bill: any;
  isRTL: boolean;
  textAlignClass: string;
  currency: (v: number | string) => string;
  t: (key: string) => string;
  couponDiscount?: any;
  currentCoupon?: any;
}

const BillPreview: React.FC<BillPreviewProps> = ({
  bill,
  isRTL,
  textAlignClass,
  currency,
  t,
  couponDiscount,
  currentCoupon,
}) => {
  return (
    <div className="bg-gray-50 rounded-xl shadow-inner p-6 mt-8">
      <h3 className={`text-xl font-semibold mb-4 ${textAlignClass}`}>
        💳 {t("cart.billPreview")}
      </h3>

      {/* 🔍 شرح الخصم */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
        <p className={textAlignClass}>
          💡{" "}
          {couponDiscount
            ? t("cart.couponExplanation")
            : t("cart.platformDiscountExplanation")}
        </p>
      </div>

      {/* 🏬 تفاصيل المتاجر */}
      {bill.stores.map((s: any) => (
        <div key={s.storeId} className="border-b pb-4 mb-4">
          <div
            className={`flex justify-between items-center mb-2 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <span className="font-medium">
              {s.store?.name || t("cart.unknownStore")}
            </span>
            <span className="text-sm text-gray-600">
              {currency(s.subtotal)} {t("currency.egp")}
            </span>
          </div>

          <div className={`space-y-1 text-sm ${textAlignClass}`}>
            {/* 💸 الخصم */}
            {s.discountAmount && s.discountAmount > 0 && (
              <div className="text-green-700 font-medium flex items-center gap-1">
                <DiscountIcon fontSize="small" />
                {couponDiscount
                  ? t("cart.couponDiscount")
                  : t("cart.platformDiscount")}
                : -{currency(s.discountAmount)} {t("currency.egp")}
              </div>
            )}

            {/* 🚚 الرسوم */}
            <div>
              {t("cart.deliveryFee")}: {currency(s.deliveryFee)}{" "}
              {t("currency.egp")}
            </div>
            <div>
              {t("cart.transactionFee")}:{" "}
              {currency(s.feesObj.totalFee)} {t("currency.egp")}
            </div>

            {/* 🔁 قبل / بعد الكوبون */}
            {s.discountedSubtotal !== undefined && (
              <>
                <div className="line-through text-gray-400">
                  {t("cart.priceBeforeCoupon")}:{" "}
                  {currency(s.subtotal + (s.discountAmount || 0))}{" "}
                  {t("currency.egp")}
                </div>
                <div className="text-green-700 font-medium">
                  {t("cart.priceAfterCoupon")}:{" "}
                  {currency(s.discountedSubtotal)} {t("currency.egp")}
                </div>
              </>
            )}

            {/* ✅ إجمالي المتجر */}
            <div className="pt-2 mt-2 border-t font-semibold">
              {t("cart.total")}: {currency(s.totalWithFees)}{" "}
              {t("currency.egp")}
            </div>
          </div>
        </div>
      ))}

      {/* 🧮 الإجمالي */}
      <div className="mt-6 space-y-2">
        <div
          className={`flex justify-between text-gray-600 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <span>{t("cart.subtotal")}</span>
          <span>
            {currency(bill.grand.subtotal)} {t("currency.egp")}
          </span>
        </div>

        <div
          className={`flex justify-between text-gray-600 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <span>{t("cart.totalFees")}</span>
          <span>
            {currency(bill.grand.totalFees)} {t("currency.egp")}
          </span>
        </div>

        {couponDiscount && (
          <div
            className={`flex justify-between text-green-700 font-medium ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <span className="flex items-center gap-1">
              <InfoIcon fontSize="small" /> {t("cart.extraSavings")}
            </span>
            <span>
              -{currency(bill.grand.discountAmount)} {t("currency.egp")}
            </span>
          </div>
        )}

        <div className="border-t pt-3 mt-2"></div>

        <div
          className={`flex justify-between text-lg font-bold ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <span>{t("cart.grandTotal")}</span>
          <span>
            {currency(
              couponDiscount
                ? bill.grand.totalAfterDiscount
                : bill.grand.total
            )}{" "}
            {t("currency.egp")}
          </span>
        </div>
      </div>

      <p className={`text-sm text-gray-600 mt-2 ${textAlignClass}`}>
        {t("cart.checkoutNote")}
      </p>
    </div>
  );
};

export default BillPreview;
